import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'
import { FC, useCallback, useEffect, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { useGetAllRaceTeams } from '../api/raceteam-API'
import {
  useCreateUser,
  useDeleteUser,
  useGetUserLazy,
  useGetUsers,
  useUpdateUser,
  useUpdateUserByRecord
} from '../api/user-API'
import { PlusIcon } from '../components/Icons'
import IconButton from '../components/buttons/IconButton'
import SaveButton from '../components/buttons/SaveButton'
import UserEditModal from '../components/user/UserEditModal'
import { useDebounce } from '../hooks/useDebounce'
import { protoService } from '../proto/ProtoService'
import routes from '../routing/routes'
import { RaceTeam, User } from '../types/types'
import { queryString2JsonString } from '../types/util'
import { currentUserState } from '../utils/state-atoms'
import { isReadOnlyRole } from '../utils/roles'
import { DropdownStyle } from '../utils/styling-constants'

// User role options matching the backend UserRoleType enum
const USER_ROLES = [
  { label: 'Aero Engineer', value: 'aero-engineer' },
  { label: 'Methodology Specialist', value: 'methodology-specialist' },
  { label: 'Developer', value: 'developer' },
  { label: 'Admin', value: 'admin' },
  { label: 'Read-Only', value: 'read-only' },
  { label: 'Resubmit-Only', value: 'resubmit-only' },
  { label: 'Dashboard-Only', value: 'dashboard-only' }
]

const ALL_OPTION_VALUE = '__all__'

type TeamAssignment = {
  raceTeamId: string
  rescaleToken: string
}

type NewTeamAssignment = {
  raceTeamId: string
  role: string
  rescaleToken?: string
}

type ModifiedTeamAssignment = {
  recordId: string
  raceTeamId: string
  role: string
  rescaleToken?: string
}

type RemovedTeamAssignment = {
  recordId: string
  raceTeamId: string
}

type UserForm = {
  firstName: string
  lastName: string
  email: string
  role: string
}

const Users: FC = () => {
  const [mode, setMode] = useState('')
  const [users, setUsers] = useState<User[]>([])
  const [raceTeams, setRaceTeams] = useState<RaceTeam[]>([])
  const [bulkTeamAssignments, setBulkTeamAssignments] = useState<TeamAssignment[]>([
    { raceTeamId: '', rescaleToken: '' }
  ])
  const [searchText, setSearchText] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>(ALL_OPTION_VALUE)
  const [teamFilter, setTeamFilter] = useState<string>(ALL_OPTION_VALUE)
  const debouncedSearchText = useDebounce(searchText, 300)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [drawerVisible, setDrawerVisible] = useState(false)

  const [{ loading: loadingUsers }, fetchUsers] = useGetUsers()
  const [{ loading: creating }, createUser] = useCreateUser()
  const [, updateUser] = useUpdateUser(selectedUser?.email || '')
  const [, updateUserByRecord] = useUpdateUserByRecord()
  const [, fetchUsersByEmail] = useGetUserLazy()
  const [, deleteUser] = useDeleteUser()
  const [, fetchRaceTeams] = useGetAllRaceTeams()

  const currentUser = useRecoilValue(currentUserState)
  const isReadOnly = isReadOnlyRole(currentUser?.role?.roleId)
  const navigate = useNavigate()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<UserForm>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      role: 'aero-engineer'
    }
  })

  const loadUsers = useCallback(() => {
    const params: Record<string, any> = {
      limit: 1000,
      allTeams: true
    }

    const normalizedRoleFilter = roleFilter === ALL_OPTION_VALUE ? '' : roleFilter
    const normalizedTeamFilter = teamFilter === ALL_OPTION_VALUE ? '' : teamFilter
    const trimmedSearch = debouncedSearchText.trim()
    const shouldApplyFilter = Boolean(normalizedRoleFilter || normalizedTeamFilter || trimmedSearch)

    if (shouldApplyFilter) {
      const filter: Record<string, any> = {}
      const andConditions: Record<string, any>[] = []

      // Add team filter
      const teamCondition = normalizedTeamFilter || 'all'
      if (teamCondition !== 'all') {
        andConditions.push({ raceTeamId: teamCondition })
      } else {
        filter.raceTeamId = 'all' // Signal to backend to skip team filtering
      }

      // Add role filter
      if (normalizedRoleFilter) {
        andConditions.push({ 'role.roleId': normalizedRoleFilter })
      }

      // Add text search filter (uses MongoDB text index)
      if (trimmedSearch) {
        andConditions.push({ $text: { $search: trimmedSearch } })
      }

      // Build final filter - use $and if we have multiple conditions
      if (andConditions.length > 0) {
        if (andConditions.length === 1 && !filter.raceTeamId) {
          Object.assign(filter, andConditions[0])
        } else {
          filter.$and = andConditions
        }
      }

      params.query = JSON.stringify(filter)
    }

    fetchUsers({ params })
      .then(res => {
        const { data } = res
        if (data && data.protoBytesList) {
          const tmpList = data.protoBytesList.map((item: string) => {
            return protoService.deSerialize(item, 'user')
          })
          setUsers(tmpList)
        }
      })
      .catch(err => {
        console.error(err)
        toast.error('Error loading users.')
      })
  }, [debouncedSearchText, fetchUsers, roleFilter, teamFilter])

  const loadRaceTeams = useCallback(() => {
    fetchRaceTeams()
      .then(res => {
        const { data } = res
        if (data && data.protoBytesList) {
          const tmpList = data.protoBytesList.map((item: string) => {
            return protoService.deSerialize(item, 'raceteam')
          })
          setRaceTeams(tmpList)
        } else {
          console.warn('No race team data returned from API', data)
        }
      })
      .catch(err => {
        console.error('Error fetching race teams:', err)
        toast.error('Error loading race teams.')
      })
  }, [fetchRaceTeams])

  useEffect(() => {
    loadUsers()
    loadRaceTeams()
  }, [loadUsers, loadRaceTeams])

  // Group users by email to show each person once with multiple teams
  const groupedUsers = users.reduce(
    (acc, user) => {
      const email = user.email
      if (!acc[email]) {
        acc[email] = {
          ...user,
          teams: []
        } as User & { teams: Array<{ raceTeamId: string; role: any }> }
      }
      acc[email].teams.push({
        raceTeamId: user.raceTeamId,
        role: user.role
      })
      return acc
    },
    {} as Record<string, User & { teams: Array<{ raceTeamId: string; role: any }> }>
  )

  const uniqueUsers = Object.values(groupedUsers)

  const raceTeamOptions = raceTeams.map(team => ({
    label: team.raceTeamId || team.name || team._id,
    value: team.raceTeamId || team._id
  }))

  const roleFilterOptions = [{ label: 'All Roles', value: ALL_OPTION_VALUE }, ...USER_ROLES]
  const raceTeamFilterOptions = [
    { label: 'All Teams', value: ALL_OPTION_VALUE },
    ...raceTeamOptions
  ]

  const handleNewUser = () => {
    setMode('create')
    reset({
      firstName: '',
      lastName: '',
      email: '',
      role: 'aero-engineer'
    })
    setBulkTeamAssignments([{ raceTeamId: '', rescaleToken: '' }])
  }

  const handleCancel = () => {
    setMode('')
    reset()
    setBulkTeamAssignments([{ raceTeamId: '', rescaleToken: '' }])
  }

  const handleClearFilters = () => {
    setSearchText('')
    setRoleFilter(ALL_OPTION_VALUE)
    setTeamFilter(ALL_OPTION_VALUE)
  }

  const handleEditUser = async (user: User) => {
    try {
      // Fetch all user records for this person (all their team assignments)
      // Use email as the identifier since it's always present (even for new users)
      const response = await fetchUsersByEmail(
        'query=' + queryString2JsonString(`email=${user.email}&raceTeamId=all`)
      )

      if (response?.data?.protoBytesList) {
        const allUserRecords = response.data.protoBytesList.map((item: string) => {
          return protoService.deSerialize(item, 'user')
        })

        // Set the first record as selected user, but pass all records to modal
        const primaryUser = allUserRecords[0] || user
        primaryUser.allTeamRecords = allUserRecords // Attach all records
        setSelectedUser(primaryUser)
      } else {
        setSelectedUser(user)
      }

      setDrawerVisible(true)
    } catch (err) {
      console.error('Error fetching user team assignments:', err)
      setSelectedUser(user)
      setDrawerVisible(true)
    }
  }

  const handleCloseDrawer = () => {
    setDrawerVisible(false)
    setSelectedUser(null)
  }

  const handleSaveUser = async (
    updatedUser: User,
    newTeamAssignments?: NewTeamAssignment[],
    removedAssignments?: RemovedTeamAssignment[],
    modifiedAssignments?: ModifiedTeamAssignment[]
  ) => {
    try {
      // Check if this is a full user deletion (all teams removed)
      const isFullDeletion =
        removedAssignments &&
        removedAssignments.length > 0 &&
        !newTeamAssignments?.length &&
        !modifiedAssignments?.length

      // If the primary record is being removed too, find it
      const primaryRecordId = (updatedUser as any)._id
      const primaryInRemovals = removedAssignments?.some(a => a.recordId === primaryRecordId)

      if (isFullDeletion && primaryInRemovals) {
        // Delete all team assignment records
        for (const assignment of removedAssignments) {
          try {
            await deleteUser({
              url: `/Users/${assignment.recordId}`,
              method: 'DELETE',
              headers: assignment.raceTeamId ? { gmmsraceteamid: assignment.raceTeamId } : undefined
            })
          } catch (removeError: any) {
            const errorMsg =
              removeError?.response?.data?.message ||
              removeError?.response?.data?.error ||
              'Unknown error'
            toast.error(`Failed to remove team ${assignment.raceTeamId}: ${errorMsg}`)
          }
        }

        toast.success('User successfully removed from the team.')
        setDrawerVisible(false)
        setSelectedUser(null)
        loadUsers()
        return
      }

      // Prepare the update payload for the existing user record
      console.log('Saving user with role:', updatedUser.role)
      const updateData = {
        firstName: updatedUser.firstName,
        lastName: updatedUser.lastName,
        email: updatedUser.email,
        raceTeamId: updatedUser.raceTeamId,
        role: {
          roleId: updatedUser.role?.roleId || '',
          name: updatedUser.role?.name || ''
        },
        rescaleTokens: updatedUser.rescaleTokens || [],
        cloudProviderLDAPRef: updatedUser.cloudProviderLDAPRef,
        updatedByUser: currentUser._id || 'system'
      }
      console.log('Update data role:', updateData.role)

      // Update the existing user record
      await updateUser({ data: updateData })

      // Update modified team assignments (non-primary teams with changed tokens)
      if (modifiedAssignments && modifiedAssignments.length > 0) {
        for (const assignment of modifiedAssignments) {
          try {
            const modifyData = {
              firstName: updatedUser.firstName,
              lastName: updatedUser.lastName,
              email: updatedUser.email,
              raceTeamId: assignment.raceTeamId,
              role: {
                roleId: assignment.role,
                name: USER_ROLES.find(r => r.value === assignment.role)?.label || assignment.role
              },
              rescaleTokens: assignment.rescaleToken ? [{ token: assignment.rescaleToken }] : [],
              cloudProviderLDAPRef: updatedUser.cloudProviderLDAPRef,
              updatedByUser: currentUser._id || 'system'
            }
            await updateUserByRecord({
              url: `/Users/${updatedUser.email}`,
              data: modifyData,
              headers: assignment.raceTeamId
                ? {
                    gmmsraceteamid: assignment.raceTeamId
                  }
                : undefined
            })
          } catch (modifyError: any) {
            const errorMsg =
              modifyError?.response?.data?.message ||
              modifyError?.response?.data?.error ||
              'Unknown error'
            toast.error(`Failed to update team ${assignment.raceTeamId}: ${errorMsg}`)
          }
        }
      }

      // Delete removed team assignments
      if (removedAssignments && removedAssignments.length > 0) {
        for (const assignment of removedAssignments) {
          try {
            await deleteUser({
              url: `/Users/${assignment.recordId}`,
              method: 'DELETE',
              headers: assignment.raceTeamId
                ? {
                    gmmsraceteamid: assignment.raceTeamId
                  }
                : undefined
            })
          } catch (removeError: any) {
            const errorMsg =
              removeError?.response?.data?.message ||
              removeError?.response?.data?.error ||
              'Unknown error'
            toast.error(`Failed to remove team ${assignment.raceTeamId}: ${errorMsg}`)
          }
        }
      }

      // Create new user records for additional team assignments
      let newTeamCount = 0
      let errorMessages: string[] = []

      if (newTeamAssignments && newTeamAssignments.length > 0) {
        for (const assignment of newTeamAssignments) {
          try {
            const newUserData = {
              firstName: updatedUser.firstName,
              lastName: updatedUser.lastName,
              email: updatedUser.email,
              raceTeamId: assignment.raceTeamId,
              role: assignment.role,
              rescaleToken: assignment.rescaleToken || '',
              cloudProviderLDAPRef: updatedUser.cloudProviderLDAPRef,
              createdByUser: currentUser._id || 'system'
            }

            await createUser({ data: newUserData })
            newTeamCount++
          } catch (assignmentError: any) {
            const errorMsg =
              assignmentError?.response?.data?.message ||
              assignmentError?.response?.data?.error ||
              'Unknown error'
            errorMessages.push(`${assignment.raceTeamId}: ${errorMsg}`)
          }
        }
      }

      const removedCount = removedAssignments?.length || 0
      const modifiedCount = modifiedAssignments?.length || 0

      // Show success/error messages
      if (newTeamCount > 0 || removedCount > 0 || modifiedCount > 0) {
        const parts = []
        if (newTeamCount > 0) parts.push(`${newTeamCount} new team assignment(s)`)
        if (removedCount > 0) parts.push(`${removedCount} removed team assignment(s)`)
        if (modifiedCount > 0) parts.push(`${modifiedCount} updated team assignment(s)`)
        toast.success(`User updated successfully with ${parts.join(', ')}`)
      } else {
        toast.success('User updated successfully')
      }

      if (errorMessages.length > 0) {
        errorMessages.forEach(msg => toast.error(msg))
      }

      setDrawerVisible(false)
      setSelectedUser(null)
      loadUsers()
    } catch (err: any) {
      console.error(err)
      toast.error(err?.response?.data?.error || 'Error updating user')
    }
  }

  const addBulkTeamRow = () => {
    setBulkTeamAssignments([...bulkTeamAssignments, { raceTeamId: '', rescaleToken: '' }])
  }

  const removeBulkTeamRow = (index: number) => {
    const newAssignments = bulkTeamAssignments.filter((_, i) => i !== index)
    setBulkTeamAssignments(newAssignments)
  }

  const updateBulkTeamAssignment = (index: number, field: keyof TeamAssignment, value: string) => {
    const newAssignments = [...bulkTeamAssignments]
    newAssignments[index][field] = value
    setBulkTeamAssignments(newAssignments)
  }

  const onSubmit: SubmitHandler<UserForm> = async data => {
    try {
      // Trim whitespace from user input
      const trimmedData = {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim(),
        role: data.role
      }

      // Validate trimmed data is not empty
      if (!trimmedData.firstName) {
        toast.error('First name cannot be empty or whitespace only')
        return
      }
      if (!trimmedData.lastName) {
        toast.error('Last name cannot be empty or whitespace only')
        return
      }
      if (!trimmedData.email) {
        toast.error('Email cannot be empty or whitespace only')
        return
      }

      const validAssignments = bulkTeamAssignments.filter(a => a.raceTeamId)

      if (validAssignments.length === 0) {
        toast.error('Please add at least one race team')
        return
      }

      // Create user for each team assignment
      let successCount = 0
      let errorMessages: string[] = []

      for (const assignment of validAssignments) {
        try {
          const userData = {
            firstName: trimmedData.firstName,
            lastName: trimmedData.lastName,
            email: trimmedData.email,
            raceTeamId: assignment.raceTeamId,
            role: trimmedData.role,
            rescaleToken: assignment.rescaleToken || '',
            cloudProviderLDAPRef: '', // Left blank - will be populated when user first logs in
            createdByUser: currentUser._id || 'system'
          }

          await createUser({ data: userData })
          successCount++
        } catch (assignmentError: any) {
          const errorMsg =
            assignmentError?.response?.data?.message ||
            assignmentError?.response?.data?.error ||
            'Unknown error'
          errorMessages.push(`${assignment.raceTeamId}: ${errorMsg}`)
        }
      }

      if (successCount > 0) {
        toast.success(`User created with ${successCount} team assignment(s)`)
        loadUsers()
        handleCancel()
      }

      if (errorMessages.length > 0) {
        errorMessages.forEach(msg => toast.error(msg))
      }
    } catch (err: any) {
      console.error(err)
      toast.error(
        err?.response?.data?.message || err?.response?.data?.error || 'Error creating user'
      )
    }
  }

  const roleBodyTemplate = (user: User) => {
    return user.role?.name || user.role?.roleId || 'N/A'
  }

  const teamBodyTemplate = (user: User & { teams?: Array<{ raceTeamId: string; role: any }> }) => {
    if (!user.teams || user.teams.length === 0) return 'N/A'

    return (
      <div className="flex flex-wrap gap-1">
        {user.teams.map((teamAssignment, idx) => {
          const team = raceTeams.find(
            t => t.raceTeamId === teamAssignment.raceTeamId || t._id === teamAssignment.raceTeamId
          )
          const teamName = team?.raceTeamId || team?.name || teamAssignment.raceTeamId
          return (
            <span
              key={`${teamAssignment.raceTeamId}-${idx}`}
              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
              {teamName}
            </span>
          )
        })}
      </div>
    )
  }

  const rescaleTokenBodyTemplate = (user: User) => {
    return user.rescaleTokens && user.rescaleTokens.length > 0 ? '✓' : '✗'
  }

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-neutral-50 dark:border-neutral-80">
        <h1 className="text-2xl font-bold text-gm-blue dark:text-light-blue min-w-0 truncate">
          User Management
        </h1>
        {mode === '' && (
          <div className="flex gap-2 ml-4 flex-shrink-0">
            {(currentUser.role?.roleId === 'admin' || currentUser.role?.roleId === 'developer') && (
              <IconButton
                severity="secondary"
                label="System Messages"
                icon={<i className="pi pi-envelope text-white" />}
                onClick={() => navigate(routes.SystemMessages.absolutePath)}
                additionalClassName="!w-auto !min-w-fit !whitespace-nowrap !px-4 !py-2"
              />
            )}
            <IconButton
              severity="secondary"
              label="New User"
              icon={<PlusIcon className="w-4 h-4 fill-white" />}
              onClick={handleNewUser}
              additionalClassName="!w-auto !min-w-fit !whitespace-nowrap !px-4 !py-2"
              disabled={isReadOnly}
            />
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto p-4">
        {mode === 'create' && (
          <div className="max-w-4xl mx-auto bg-white dark:bg-neutral-90 rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-6 text-gm-blue dark:text-light-blue">
              Create New User
            </h2>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Personal Information */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="mb-2 font-medium">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="firstName"
                    control={control}
                    rules={{ required: 'First name is required' }}
                    render={({ field }) => (
                      <InputText
                        {...field}
                        placeholder="Enter first name"
                        className={classNames({ 'p-invalid': errors.firstName })}
                      />
                    )}
                  />
                  {errors.firstName && (
                    <small className="text-red-500 mt-1">{errors.firstName.message}</small>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="mb-2 font-medium">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="lastName"
                    control={control}
                    rules={{ required: 'Last name is required' }}
                    render={({ field }) => (
                      <InputText
                        {...field}
                        placeholder="Enter last name"
                        className={classNames({ 'p-invalid': errors.lastName })}
                      />
                    )}
                  />
                  {errors.lastName && (
                    <small className="text-red-500 mt-1">{errors.lastName.message}</small>
                  )}
                </div>
              </div>

              <div className="flex flex-col">
                <label className="mb-2 font-medium">
                  Email <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    },
                    validate: value => {
                      // Validate email after trimming to ensure proper format
                      const trimmedEmail = value.trim()
                      if (!trimmedEmail) {
                        return 'Email cannot be empty'
                      }
                      // Check pattern on trimmed email
                      const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                      if (!emailPattern.test(trimmedEmail)) {
                        return 'Invalid email address'
                      }
                      return true
                    }
                  }}
                  render={({ field }) => (
                    <InputText
                      {...field}
                      type="email"
                      placeholder="Enter email address"
                      className={classNames({ 'p-invalid': errors.email })}
                    />
                  )}
                />
                {errors.email && (
                  <small className="text-red-500 mt-1">{errors.email.message}</small>
                )}
              </div>

              <div className="flex flex-col">
                <label className="mb-2 font-medium">
                  Role <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="role"
                  control={control}
                  rules={{ required: 'Role is required' }}
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      options={USER_ROLES}
                      placeholder="Select a role"
                      pt={DropdownStyle}
                      className={classNames({ 'p-invalid': errors.role })}
                    />
                  )}
                />
                {errors.role && <small className="text-red-500 mt-1">{errors.role.message}</small>}
              </div>

              {/* Team Assignments */}
              <div className="space-y-4 p-4 border border-neutral-50 dark:border-neutral-70 rounded">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Team Assignments</h3>
                  <Button
                    type="button"
                    icon={<PlusIcon />}
                    label="Add Team"
                    onClick={addBulkTeamRow}
                    className="p-button-sm p-button-outlined"
                  />
                </div>

                <div className="space-y-3">
                  {bulkTeamAssignments.map((assignment, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-3 bg-neutral-5 dark:bg-neutral-85 rounded">
                      <div className="flex-1 grid grid-cols-2 gap-3">
                        <div className="flex flex-col">
                          <label className="mb-1 text-sm font-medium">Race Team</label>
                          <Dropdown
                            value={assignment.raceTeamId}
                            options={raceTeamOptions}
                            onChange={e => updateBulkTeamAssignment(index, 'raceTeamId', e.value)}
                            placeholder="Select team"
                            filter
                            pt={DropdownStyle}
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="mb-1 text-sm font-medium">
                            Rescale Token (Optional)
                          </label>
                          <InputText
                            value={assignment.rescaleToken}
                            onChange={e =>
                              updateBulkTeamAssignment(index, 'rescaleToken', e.target.value)
                            }
                            placeholder="Enter token"
                          />
                        </div>
                      </div>
                      {bulkTeamAssignments.length > 1 && (
                        <Button
                          type="button"
                          icon="pi pi-trash"
                          onClick={() => removeBulkTeamRow(index)}
                          className="p-button-danger p-button-sm mt-6"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-neutral-50 dark:border-neutral-70">
                <Button
                  type="button"
                  label="Cancel"
                  onClick={handleCancel}
                  className="p-button-outlined"
                />
                <SaveButton label="Create User" loading={creating} />
              </div>
            </form>
          </div>
        )}

        {mode === '' && (
          <div className="bg-white dark:bg-neutral-90 rounded-lg shadow-sm border border-neutral-50 dark:border-neutral-80">
            {/* Toolbar */}
            <div className="flex items-center gap-3 p-4 bg-white dark:bg-neutral-90">
              <InputText
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="Search users..."
                className="flex-shrink-0"
                style={{ width: '400px' }}
              />

              <Dropdown
                value={roleFilter}
                options={roleFilterOptions}
                onChange={e => setRoleFilter(e.value)}
                placeholder="All Roles"
                pt={DropdownStyle}
                className="flex-shrink-0"
                style={{ width: '180px', minWidth: '180px', maxWidth: '180px' }}
              />

              <Dropdown
                value={teamFilter}
                options={raceTeamFilterOptions}
                onChange={e => setTeamFilter(e.value)}
                placeholder="All Teams"
                filter
                pt={DropdownStyle}
                className="flex-shrink-0"
                style={{ width: '180px', minWidth: '180px', maxWidth: '180px' }}
              />

              <Button
                type="button"
                icon="pi pi-filter-slash"
                onClick={handleClearFilters}
                text
                severity="secondary"
                tooltip="Clear Filters"
                tooltipOptions={{ position: 'bottom' }}
                disabled={
                  !searchText.trim() &&
                  roleFilter === ALL_OPTION_VALUE &&
                  teamFilter === ALL_OPTION_VALUE
                }
              />
            </div>

            <DataTable
              value={uniqueUsers}
              loading={loadingUsers}
              paginator
              rows={25}
              rowsPerPageOptions={[10, 25, 50, 100]}
              emptyMessage={
                <div className="flex flex-col items-center justify-center py-12 text-neutral-20">
                  <div className="bg-neutral-5 dark:bg-neutral-85 p-6 rounded-full mb-4">
                    <i className="pi pi-users text-4xl opacity-50" />
                  </div>
                  <span className="text-lg font-medium">No users found</span>
                  <span className="text-sm opacity-70 mt-1">
                    Try adjusting your search or filters
                  </span>
                </div>
              }
              onRowDoubleClick={e => handleEditUser(e.data as User)}
              className="p-datatable-sm"
              rowHover
              stripedRows
              pt={{
                headerRow: {
                  className:
                    'bg-neutral-5 dark:bg-neutral-85 text-xs font-semibold text-neutral-60 uppercase tracking-wide'
                },
                bodyRow: {
                  className: 'table-row cursor-pointer'
                },
                header: { className: 'pt-4' }
              }}>
              <Column
                field="firstName"
                header="First Name"
                sortable
                body={(d: User) => (
                  <span
                    className="font-semibold text-gm-blue dark:text-light-blue hover:underline cursor-pointer"
                    onClick={() => handleEditUser(d)}>
                    {d.firstName}
                  </span>
                )}
              />
              <Column field="lastName" header="Last Name" sortable />
              <Column
                field="email"
                header="Email"
                sortable
                body={(d: User) => (
                  <span className="text-neutral-70 dark:text-neutral-10 font-mono text-sm">
                    {d.email}
                  </span>
                )}
              />
              <Column header="Role" body={roleBodyTemplate} sortable bodyClassName="capitalize" />
              <Column header="Team" body={teamBodyTemplate} sortable />
              <Column
                header="Rescale"
                body={rescaleTokenBodyTemplate}
                align="center"
                sortable
                headerStyle={{ width: '100px' }}
              />
            </DataTable>
          </div>
        )}

        <UserEditModal
          visible={drawerVisible}
          user={selectedUser}
          raceTeams={raceTeams}
          onHide={handleCloseDrawer}
          onSave={handleSaveUser}
        />
      </div>
    </div>
  )
}

export default Users
